import React, { useState } from 'react';
import axios from 'axios';

const BookingForm = ({ user, onSuccess }) => {
  const [formData, setFormData] = useState({
    chef_id: 'demo-chef-id', // In production, user would select from available chefs
    meal_type: 'dinner',
    guest_count: 4,
    date: '',
    service_city: 'Atlanta',
    service_state: 'GA',
    service_zip: '30301',
    bartender: '',
    zip_allowlist: []
  });
  const [guestNotes, setGuestNotes] = useState({});
  const [showGuestNotes, setShowGuestNotes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Calculate pricing based on new structure
  const calculateTotal = () => {
    const count = formData.guest_count;
    const mealType = formData.meal_type;
    let baseFee = 0;
    
    // Handle 2 and 4 person bookings
    if (count === 2) {
      baseFee = mealType === 'lunch' ? 175 : 425;
    } else if (count === 4) {
      baseFee = mealType === 'lunch' ? 400 : 825;
    } else if (count >= 5 && count <= 9) {
      // For 5-9 guests: $600-$1200 range (complexity-based)
      // Using mid-range for display
      baseFee = 900;
    } else if (count >= 10) {
      // 10+ guests must contact office
      return null; // Indicates booking not available online
    } else if (count === 1) {
      baseFee = mealType === 'lunch' ? 175 : 425; // Same as 2 person
    } else if (count === 3) {
      baseFee = mealType === 'lunch' ? 287.50 : 625; // Average between 2 and 4
    }
    
    // Add $26 processing fee
    baseFee += 26;
    
    return baseFee;
  };

  const calculateGroceryLimit = () => {
    const count = formData.guest_count;
    if (count >= 5 && count <= 9) {
      return { min: 800, max: 950, default: 900 };
    } else if (count >= 10) {
      return { min: count * 120, max: count * 120, default: count * 120, catering: true };
    } else {
      return { min: count * 150, max: count * 150, default: count * 150 };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate 10+ guests
    if (formData.guest_count >= 10) {
      setError('For 10 or more guests, please contact our office directly to arrange your booking.');
      setLoading(false);
      return;
    }

    // Validate booking time restrictions
    const bookingDate = new Date(formData.date);
    const currentDate = new Date();
    const bookingHour = bookingDate.getHours();
    const bookingMinutes = bookingDate.getMinutes();
    const currentHour = currentDate.getHours();
    const currentMinutes = currentDate.getMinutes();

    // Check if booking is for today
    if (bookingDate.toDateString() === currentDate.toDateString()) {
      if (formData.meal_type === 'lunch') {
        // Lunch must be ordered before 7:30 AM
        if (currentHour > 7 || (currentHour === 7 && currentMinutes >= 30)) {
          setError('⏰ Lunch bookings must be placed before 7:30 AM on the day of service. Please select a future date or contact our office.');
          setLoading(false);
          return;
        }
      } else if (formData.meal_type === 'dinner') {
        // Dinner must be ordered by 11 AM
        if (currentHour >= 11) {
          setError('⏰ Dinner bookings must be placed by 11:00 AM on the day of service. Please select a future date or contact our office.');
          setLoading(false);
          return;
        }
      }
    }

    try {
      const response = await axios.post('/booking/', {
        customer_id: user.id,
        ...formData,
        guest_notes: guestNotes  // Include individual guest preferences
      });
      
      alert(`Booking created! Total: $${(response.data.total_cents / 100).toFixed(2)}`);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const total = calculateTotal();
  const groceryLimit = calculateGroceryLimit();

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
      {error && (
        <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm" data-testid="error-message">
          {error}
        </div>
      )}

      {/* Pricing Display */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-lg p-4">
        <h3 className="font-bold text-lg mb-3">📋 Booking Summary</h3>
        
        {formData.guest_count >= 10 ? (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-3">
            <p className="font-bold text-blue-900 mb-2">📞 Contact Office for Catering</p>
            <p className="text-sm text-blue-800 mb-2">
              For groups of <strong>10-100 guests</strong>, we offer catering services with special pricing:
            </p>
            <ul className="text-sm text-blue-800 mb-2 ml-4 list-disc">
              <li><strong>Basic:</strong> $75/plate - 3-course meal</li>
              <li><strong>Premium:</strong> $100/plate - 4-course meal</li>
              <li><strong>Deluxe:</strong> $150/plate - 5-course meal</li>
            </ul>
            <p className="text-sm text-blue-800 mb-2">
              Plus: $300 setup fee + $125 delivery fee
            </p>
            <div className="bg-blue-100 rounded p-3 mt-3">
              <p className="font-bold text-blue-900 text-lg">📞 Call us at:</p>
              <p className="text-2xl font-bold text-blue-900">614-517-6320</p>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Meal Type:</span>
                <span className="font-semibold capitalize">{formData.meal_type}</span>
              </div>
              <div className="flex justify-between">
                <span>Number of Guests:</span>
                <span className="font-semibold">{formData.guest_count} {formData.guest_count === 1 ? 'guest' : 'guests'}</span>
              </div>
              
              {formData.guest_count >= 5 && formData.guest_count <= 9 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 my-2">
                  <p className="text-xs text-yellow-800 font-semibold">
                    ℹ️ 5-9 Guest Pricing: $600-$1200 (complexity-based)
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Shown price is mid-range. Final price depends on meal complexity.
                  </p>
                </div>
              ) : null}
              
              <div className="flex justify-between">
                <span>Chef Service Fee:</span>
                <span className="font-semibold">${(total - 26).toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between text-gray-600">
                <span>Processing Fee:</span>
                <span className="font-semibold">$26.00</span>
              </div>
              
              <div className="border-t border-orange-300 pt-2 mt-2 flex justify-between text-lg">
                <span className="font-bold">Total Due Now:</span>
                <span className="font-bold text-orange-600">${total ? total.toFixed(2) : '0.00'}</span>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-orange-300">
              <p className="text-xs font-semibold text-gray-700 mb-1">🛒 Grocery Costs (Separate):</p>
              <p className="text-sm text-gray-600">
                Grocery costs will be charged separately based on actual receipts submitted by the chef.
              </p>
              <p className="text-xs text-gray-500 mt-1 italic">
                Note: The above price does NOT include groceries
              </p>
            </div>
            
            <div className="mt-3 pt-3 border-t border-orange-300">
              <p className="text-xs font-semibold text-red-600 mb-1">⏰ Ordering Deadlines:</p>
              <p className="text-xs text-gray-600">
                • Lunch: Must order before <strong>7:30 AM</strong> on service day<br/>
                • Dinner: Must order by <strong>11:00 AM</strong> on service day
              </p>
            </div>
          </>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Meal Type</label>
        <select
          value={formData.meal_type}
          onChange={(e) => setFormData({ ...formData, meal_type: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
          data-testid="meal-type-select"
        >
          <option value="lunch">Lunch</option>
          <option value="dinner">Dinner</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Number of Guests</label>
        <input
          type="number"
          min="1"
          max="30"
          value={formData.guest_count}
          onChange={(e) => {
            const newCount = parseInt(e.target.value);
            setFormData({ ...formData, guest_count: newCount });
            // Reset guest notes if count changes
            if (newCount <= 9) {
              const newNotes = {};
              for (let i = 1; i <= newCount; i++) {
                newNotes[i] = guestNotes[i] || '';
              }
              setGuestNotes(newNotes);
            }
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
          data-testid="guest-count-input"
        />
        {formData.guest_count >= 10 && (
          <p className="text-xs text-blue-600 mt-1 font-semibold">
            📞 10+ guests require office contact for booking
          </p>
        )}
        {formData.guest_count >= 1 && formData.guest_count <= 9 && (
          <button
            type="button"
            onClick={() => setShowGuestNotes(!showGuestNotes)}
            className="mt-2 text-sm text-orange-600 hover:text-orange-700 font-medium"
          >
            {showGuestNotes ? '▼ Hide' : '▶'} Add individual preferences/notes for each person
          </button>
        )}
      </div>

      {/* Individual Guest Notes */}
      {showGuestNotes && formData.guest_count >= 1 && formData.guest_count <= 9 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Individual Guest Preferences</h4>
          <p className="text-xs text-gray-600 mb-3">
            Add special dietary restrictions, allergies, or meal preferences for each person
          </p>
          <div className="space-y-3">
            {Array.from({ length: formData.guest_count }, (_, i) => i + 1).map((guestNum) => (
              <div key={guestNum}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Person {guestNum}
                </label>
                <input
                  type="text"
                  value={guestNotes[guestNum] || ''}
                  onChange={(e) => setGuestNotes({ ...guestNotes, [guestNum]: e.target.value })}
                  placeholder="e.g., No shellfish, gluten-free, vegetarian"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
        <input
          type="datetime-local"
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
          data-testid="date-input"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Bartender (Optional)</label>
        <select
          value={formData.bartender}
          onChange={(e) => setFormData({ ...formData, bartender: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
          data-testid="bartender-select"
        >
          <option value="">None</option>
          <option value="2hr">$150 / 2 hours</option>
          <option value="4hr">$300 / 4 hours</option>
        </select>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
          <input
            type="text"
            value={formData.service_city}
            onChange={(e) => setFormData({ ...formData, service_city: e.target.value })}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
            data-testid="city-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
          <input
            type="text"
            value={formData.service_state}
            onChange={(e) => setFormData({ ...formData, service_state: e.target.value })}
            required
            maxLength="2"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
            data-testid="state-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
          <input
            type="text"
            value={formData.service_zip}
            onChange={(e) => setFormData({ ...formData, service_zip: e.target.value })}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
            data-testid="zip-input"
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="submit-booking-button"
      >
        {loading ? 'Creating Booking...' : 'Book Chef'}
      </button>
    </form>
  );
};

export default BookingForm;
